/**
 * PATH: packages/ui/src/kanban/components/card-detail/LabelManagerPanel.tsx
 * FILE_ID: UI-KANBAN-LABEL-MANAGER-001
 * PURPOSE: The org label REGISTRY side editor — rename, recolour (fill +
 *   optional two-colour accent border), archive, and create labels, in one
 *   panel. Editing a label recolours every card wearing it instantly
 *   (strict colour=label tie: cards reference the Tag row).
 * WHY: ARC-4 U09 — client (page 6): "with color it is editing on the side
 *   to be able to edit labels and create new ones" + "two-color label
 *   concepts". Until now labels could only be created (TagsSection inline
 *   composer) — never edited — so a typo'd or wrongly-coloured label was
 *   permanent.
 * FLOW:
 *   ┌────────────┐  edit   ┌──────────────────┐  save   ┌──────────────────┐
 *   │ label rows │ ──────▶ │ name + fill +    │ ──────▶ │ api.updateTag    │
 *   │ (org Tags) │         │ accent palettes  │         │ PATCH /tags/<id>/│
 *   └────────────┘         └──────────────────┘         └──────────────────┘
 * DEPENDENCIES:
 *   - ../../api KanbanApi.updateTag/createTag: the write paths.
 * RELATED:
 *   - ./TagsSection.tsx — hosts this behind the picker's "Edit labels" toggle.
 */
import { useState } from 'react'
import { Archive, Check, Loader2, Pencil, Plus, X } from 'lucide-react'

import type { KanbanApi } from '../../api'
import type { Tag } from '../../types'

export const LABEL_FILL_PALETTE = [
  '#ef4444', '#f97316', '#eab308', '#84cc16',
  '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6',
  '#8b5cf6', '#ec4899', '#64748b', '#0f172a',
]

/** Accent (border) choices — darker companions so the two-colour combo
 *  keeps contrast. '' = no accent (classic single-colour label). */
export const LABEL_ACCENT_PALETTE = [
  '', '#7f1d1d', '#7c2d12', '#713f12', '#365314',
  '#14532d', '#134e4a', '#164e63', '#1e3a8a',
  '#4c1d95', '#831843', '#1e293b',
]

/** The two-colour chip preview — fill + accent border, shared shape with
 *  the live chips in TagsSection so what you edit is what cards wear. */
export function LabelChipPreview({ tag }: { tag: Pick<Tag, 'name' | 'color' | 'accent_color'> }) {
  const fill = tag.color || '#94a3b8'
  const border = tag.accent_color || `${fill}55`
  return (
    <span
      className="inline-flex max-w-full items-center truncate rounded-sm px-1.5 py-0.5 text-[11px] font-medium"
      style={{ background: `${fill}26`, color: fill, border: `1.5px solid ${border}` }}
    >
      {tag.name || 'Label'}
    </span>
  )
}

function PaletteRow({
  label, options, value, onPick, allowNone,
}: {
  label: string
  options: string[]
  value: string
  onPick: (c: string) => void
  allowNone?: boolean
}) {
  return (
    <div>
      <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500">
        {label}
      </div>
      <div className="flex flex-wrap gap-1">
        {options.map((c) => (
          <button
            key={c || '__none__'}
            type="button"
            aria-label={c ? `${label} ${c}` : `No ${label.toLowerCase()}`}
            onClick={() => onPick(c)}
            className={[
              'flex h-4 w-4 items-center justify-center rounded transition-transform',
              value === c ? 'ring-2 ring-offset-1 ring-blue-500 scale-110' : '',
              !c ? 'border border-dashed border-neutral-300 text-neutral-400 dark:border-neutral-600' : '',
            ].join(' ')}
            style={c ? { background: c } : undefined}
          >
            {!c && allowNone && <X className="h-2.5 w-2.5" />}
          </button>
        ))}
      </div>
    </div>
  )
}

interface RowDraft {
  name: string
  color: string
  accent: string
}

export interface LabelManagerPanelProps {
  orgTags: Tag[]
  api: KanbanApi
  orgId?: number
  /** Fired after any registry write (create/update/archive) so the host
   *  invalidates the org tags query — every card chip repaints. */
  onRegistryChanged?: () => void
}

export function LabelManagerPanel({
  orgTags,
  api,
  orgId,
  onRegistryChanged,
}: LabelManagerPanelProps) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<RowDraft>({ name: '', color: '', accent: '' })
  const [busyId, setBusyId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const startEdit = (tag: Tag) => {
    setEditingId(tag.id)
    setDraft({ name: tag.name, color: tag.color || LABEL_FILL_PALETTE[7], accent: tag.accent_color ?? '' })
    setError(null)
  }

  const saveEdit = async (tag: Tag) => {
    const name = draft.name.trim()
    if (!name) return
    setBusyId(tag.id)
    setError(null)
    try {
      await api.updateTag(tag.id, {
        name,
        color: draft.color,
        accent_color: draft.accent,
      })
      setEditingId(null)
      onRegistryChanged?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the label.')
    } finally {
      setBusyId(null)
    }
  }

  const archive = async (tag: Tag) => {
    setBusyId(tag.id)
    setError(null)
    try {
      await api.updateTag(tag.id, { archived: true })
      if (editingId === tag.id) setEditingId(null)
      onRegistryChanged?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not archive the label.')
    } finally {
      setBusyId(null)
    }
  }

  const create = async () => {
    const name = draft.name.trim()
    if (!name || orgId === undefined) return
    setBusyId('__create__')
    setError(null)
    try {
      await api.createTag({
        organisation: orgId,
        name,
        path: name.toLowerCase().replace(/\s+/g, '-'),
        color: draft.color || LABEL_FILL_PALETTE[7],
        accent_color: draft.accent,
      })
      setCreating(false)
      setDraft({ name: '', color: '', accent: '' })
      onRegistryChanged?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the label.')
    } finally {
      setBusyId(null)
    }
  }

  const editorBody = (
    <div className="space-y-1.5">
      <input
        type="text"
        value={draft.name}
        autoFocus
        placeholder="Label name…"
        onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
        className="w-full rounded border border-neutral-200 bg-white px-1.5 py-1 text-xs dark:border-neutral-800 dark:bg-neutral-900"
      />
      <PaletteRow
        label="Fill"
        options={LABEL_FILL_PALETTE}
        value={draft.color}
        onPick={(c) => setDraft((d) => ({ ...d, color: c }))}
      />
      <PaletteRow
        label="Accent"
        options={LABEL_ACCENT_PALETTE}
        value={draft.accent}
        onPick={(c) => setDraft((d) => ({ ...d, accent: c }))}
        allowNone
      />
      <LabelChipPreview tag={{ name: draft.name || 'Preview', color: draft.color, accent_color: draft.accent }} />
    </div>
  )

  return (
    <div className="space-y-1.5" data-testid="kanban-label-manager">
      <div className="max-h-56 space-y-0.5 overflow-y-auto pr-0.5">
        {orgTags.length === 0 && (
          <p className="px-1 py-1 text-[11px] text-neutral-400">No labels in this org yet.</p>
        )}
        {orgTags.map((tag) => (
          <div key={tag.id} className="rounded-md px-1 py-1 hover:bg-neutral-50 dark:hover:bg-neutral-900/60">
            {editingId === tag.id ? (
              <div className="space-y-1.5">
                {editorBody}
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => void saveEdit(tag)}
                    disabled={!draft.name.trim() || busyId === tag.id}
                    className="inline-flex items-center gap-1 rounded bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {busyId === tag.id ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Check className="h-2.5 w-2.5" />}
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingId(null)}
                    className="rounded px-2 py-0.5 text-[11px] text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <div className="min-w-0 flex-1">
                  <LabelChipPreview tag={tag} />
                </div>
                <button
                  type="button"
                  aria-label={`Edit label ${tag.name}`}
                  onClick={() => startEdit(tag)}
                  className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 dark:hover:bg-neutral-800"
                >
                  <Pencil className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  aria-label={`Archive label ${tag.name}`}
                  disabled={busyId === tag.id}
                  onClick={() => void archive(tag)}
                  className="rounded p-1 text-neutral-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:hover:bg-red-950/40"
                >
                  {busyId === tag.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Archive className="h-3 w-3" />}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      {orgId !== undefined && (
        <div className="border-t border-neutral-200 pt-1.5 dark:border-neutral-800">
          {!creating ? (
            <button
              type="button"
              onClick={() => {
                setCreating(true)
                setEditingId(null)
                setDraft({ name: '', color: LABEL_FILL_PALETTE[7], accent: '' })
              }}
              className="flex w-full items-center gap-1.5 rounded px-1 py-1 text-left text-xs font-medium text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950/30"
            >
              <Plus className="h-3 w-3" /> New label
            </button>
          ) : (
            <div className="space-y-1.5 px-1 py-1">
              {editorBody}
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => void create()}
                  disabled={!draft.name.trim() || busyId === '__create__'}
                  className="inline-flex items-center gap-1 rounded bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {busyId === '__create__' && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => setCreating(false)}
                  className="rounded px-2 py-0.5 text-[11px] text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {error && <p className="text-[11px] text-red-600 dark:text-red-400">{error}</p>}
    </div>
  )
}
